import { zodResolver } from "@hookform/resolvers/zod";
import {
    ActionMenu,
    Icon,
    useToasts,
} from "@twinkltech/curriculum-architect-component-library";
import {
    ApiResponse,
    Subject,
    UpdateSubjectStatusPayload,
} from "@twinkltech/curriculum-architect-domain-hooks";
import { ComponentProps, FormEvent, useId, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import z from "zod";

import { Tag } from "@/components/Tag/Tag";

import { COMMON_NS } from "@/i18n/common/common.i18n";
import { captureAppError, captureAppWarning } from "@/utils/monitoring/sentry";

type Statuses = "not_yet_planned" | "planning_in_progress" | "planned";

type StatusDropdownOption<T extends string> = {
  label: string;
  value: T;
} & ComponentProps<typeof Tag>;
export type StatusDropdownItems<T extends string> = StatusDropdownOption<T>[];

const DropdownResolverSchema = z.object({
  status: z.enum([
    "not_yet_planned",
    "planning_in_progress",
    "planned",
  ] as const),
});

type StatusDropdownProps = {
  asDropdown?: boolean;
  defaultValue?: Statuses;
  curriculumId: string;
  subjectId: string;
  errorMessages?: Record<string, string>;
  onStatusUpdate: (params: {
    curriculumId: string;
    subjectId: string;
    payload: UpdateSubjectStatusPayload;
  }) => ApiResponse<Partial<Subject | void>>;
} & ComponentProps<typeof Tag>;

const statuses: StatusDropdownItems<Statuses> = [
  {
    label: "Not yet planned",
    variant: "not_yet_planned",
    value: "not_yet_planned",
  },
  {
    label: "Planning in progress",
    variant: "planning_in_progress",
    value: "planning_in_progress",
  },
  {
    label: "Planned",
    variant: "planned",
    value: "planned",
  },
] as const;

/* NOTE: If you need another version of this, clone it and modify as needed
 * DO NOT modify this one to work as a generic... it is more trouble than it's worth
 * ... trust me :)
 */
export const SubjectStatusDropdown = ({
  asDropdown = true,
  curriculumId,
  defaultValue,
  disabled,
  errorMessages,
  onStatusUpdate,
  subjectId,
  ...props
}: StatusDropdownProps) => {
  type DropdownFormData = z.infer<typeof DropdownResolverSchema>;

  const { control, handleSubmit } = useForm<
    DropdownFormData,
    unknown,
    DropdownFormData
  >({
    resolver: zodResolver<DropdownFormData, unknown, DropdownFormData>(
      DropdownResolverSchema
    ),
  });

  const { t } = useTranslation();
  const { addErrorToast, addSuccessToast } = useToasts();

  const [isDisabled, setIsDisabled] = useState(false);

  // Used to display the selected status until the response is received
  const [optimisiticStatus, setOptimisiticStatus] = useState<Statuses | null>(
    null
  );

  const dropdownContainerClasses = [
    "flex flex-col gap-2 bg-white p-2 rounded-lg shadow-md",
  ];
  const triggerClassNames = [
    "inline-flex items-center gap-1 pr-1 cursor-pointer",
  ];

  const isDefaultStatusValueValid =
    defaultValue && statuses.some((status) => status.value === defaultValue);

  const selectedStatusId = isDefaultStatusValueValid ? defaultValue : null;

  const selectedItem = isDefaultStatusValueValid
    ? statuses.find(
        (status) => status.value === (optimisiticStatus ?? defaultValue)
      ) ?? statuses[0]
    : { label: "Choose an item", variant: undefined };

  const onSubmitSuccess = async (
    data: { status: Statuses },
    e: FormEvent,
    closeMenu: () => void
  ) => {
    e.preventDefault();
    setIsDisabled(true);
    setOptimisiticStatus(data.status);

    // We always close the menu after submission
    closeMenu();

    const response = await onStatusUpdate({
      curriculumId,
      subjectId,
      payload: data,
    });

    setOptimisiticStatus(null);
    setIsDisabled(false);

    if (response instanceof Error) {
      captureAppError(response, {
        action: "status-update",
        area: "subject",
        extra: {
          component: "SubjectStatusDropdown",
          curriculumId,
          subjectId,
        },
      });
      addErrorToast({
        message:
          errorMessages?.[response.message] ??
          t("system-messages.update.error", { ns: COMMON_NS }),
      });
    } else {
      addSuccessToast({
        message: t("system-messages.update.success", { ns: COMMON_NS }),
      });
    }
  };

  const tagLabelAriaId = useId();

  return asDropdown ? (
    <ActionMenu>
      <ActionMenu.Trigger disabled={disabled}>
        <Tag
          className={triggerClassNames.filter(Boolean).join(" ")}
          {...props}
          aria-labelledby={tagLabelAriaId}
          disabled={isDisabled || disabled}
          variant={selectedItem.variant}
        >
          <span id={tagLabelAriaId}>{selectedItem.label}</span>
          <Icon aria-hidden fontSize="inherit" icon="KeyboardArrowDown" />
        </Tag>
      </ActionMenu.Trigger>
      <ActionMenu.List
        className={dropdownContainerClasses.filter(Boolean).join(" ")}
      >
        {({ closeMenu }) => (
          <form
            className="flex flex-col gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              return handleSubmit(
                // OnValidSubmission
                (data) => onSubmitSuccess(data, e, closeMenu),
                // OnInvalidSubmission. Keeping like this for now
                (formError) => {
                  // Serialize form errors to avoid non-serializable refs in Sentry
                  const formErrorSummary = Object.entries(formError)
                    .filter(([, error]) => error)
                    .map(([field, error]) => ({
                      field,
                      type: error?.type,
                      message: error?.message,
                    }));
                  captureAppWarning(
                    "SubjectStatusDropdown invalid form",
                    {
                      action: "status-update",
                      area: "subject",
                      extra: {
                        component: "SubjectStatusDropdown",
                        curriculumId,
                        formErrorSummary,
                        subjectId,
                      },
                    }
                  );
                }
              )();
            }}
          >
            {/* TODO - May need to create brand new component for dropdown options usecase to hook up with RHF */}
            <Controller
              control={control}
              name="status"
              render={({ field: { onChange } }) => {
                return (
                  <>
                    {statuses.map((tag, key) => {
                      const { label, value, ...tagProps } = tag;

                      return (
                        value !== selectedStatusId && (
                          <div key={key}>
                            <Tag
                              as="button"
                              role="option"
                              type="submit"
                              {...tagProps}
                              // We need to use the onClick to trigger the onChange
                              onClick={(
                                e: React.MouseEvent<HTMLButtonElement>
                              ) =>
                                onChange({
                                  ...e,
                                  target: {
                                    ...e.target,
                                    // We pass the value here as the onChange does not seem to pick up button submissions
                                    value,
                                  },
                                })
                              } // send value to hook form
                            >
                              {label}
                            </Tag>
                          </div>
                        )
                      );
                    })}
                  </>
                );
              }}
            />
          </form>
        )}
      </ActionMenu.List>
    </ActionMenu>
  ) : (
    <Tag {...props} as="span" role="note" variant={selectedItem.variant}>
      {selectedItem.label}
    </Tag>
  );
};
